/**
 * Magento Integration Setup Component
 * Provides UI for configuring Magento 2 integration
 */

import React, { useState } from 'react';
import { MagentoIntegrationFactory, MagentoConnectionParams } from '../../services/integrations/ecommerce/MagentoIntegrationFactory';
import { SelectField } from "components/ui/select-field";

interface MagentoIntegrationSetupProps {
  onComplete?: (integration: any) => void;
  onCancel?: () => void;
  initialConfig?: Partial<MagentoConnectionParams>;
}

interface FormData extends MagentoConnectionParams {
  testConnection?: boolean;
}

interface ValidationErrors {
  [key: string]: string;
}

export const MagentoIntegrationSetup: React.FC<MagentoIntegrationSetupProps> = ({
  onComplete,
  onCancel,
  initialConfig = {}
}) => {
  const [formData, setFormData] = useState<FormData>({
    baseUrl: initialConfig.baseUrl || '',
    adminToken: initialConfig.adminToken || '',
    storeCode: initialConfig.storeCode || 'default',
    websiteId: initialConfig.websiteId || 1,
    storeId: initialConfig.storeId || 1,
    version: initialConfig.version || '2.4',
    enableGraphQL: initialConfig.enableGraphQL || false,
    webhookSecret: initialConfig.webhookSecret || ''
  });

  const [errors, setErrors] = useState<ValidationErrors>({});
  const [isLoading, setIsLoading] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);
  const [currentStep, setCurrentStep] = useState(1);

  const validateForm = (): boolean => {
    const validation = MagentoIntegrationFactory.validateParams(formData);
    
    if (!validation.isValid) {
      const newErrors: ValidationErrors = {};
      validation.errors.forEach(error => {
        if (error.includes('Base URL')) {
          newErrors.baseUrl = error;
        } else if (error.includes('Admin token')) {
          newErrors.adminToken = error;
        } else if (error.includes('Store code')) {
          newErrors.storeCode = error;
        } else if (error.includes('Website ID')) {
          newErrors.websiteId = error;
        } else if (error.includes('Store ID')) {
          newErrors.storeId = error;
        } else if (error.includes('Version')) {
          newErrors.version = error;
        } else if (error.includes('Webhook secret')) {
          newErrors.webhookSecret = error;
        }
      });
      setErrors(newErrors);
      return false;
    }

    setErrors({});
    return true;
  };

  const handleInputChange = (field: keyof FormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleTestConnection = async () => {
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setTestResult(null);

    try {
      const result = await MagentoIntegrationFactory.testConnection(formData);
      
      if (result.success) {
        setTestResult({
          success: true,
          message: `Successfully connected to ${result.storeInfo?.name || 'Magento store'}`
        });
        setCurrentStep(2);
      } else {
        setTestResult({
          success: false,
          message: result.error || 'Connection failed'
        });
      }
    } catch (error) {
      setTestResult({
        success: false,
        message: error instanceof Error ? error.message : 'Unknown error occurred'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleComplete = () => {
    if (!validateForm()) {
      return;
    }

    const integration = MagentoIntegrationFactory.create(formData);
    onComplete?.(integration);
  };

  const renderStep1 = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-content mb-4">
          Magento Store Connection
        </h3>
        <p className="text-sm text-content-secondary mb-6">
          Enter your Magento store details to establish a connection. You'll need admin access to generate an access token.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-6">
        <div>
          <label htmlFor="baseUrl" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Store Base URL *
          </label>
          <input
            type="url"
            id="baseUrl"
            value={formData.baseUrl}
            onChange={(e) => handleInputChange('baseUrl', e.target.value)}
            placeholder="https://yourstore.com"
            className={`mt-1 block w-full rounded-md border-line-strong shadow-sm focus:border-blue-500 focus:ring-blue-500 ${
              errors.baseUrl ? 'border-red-300 dark:border-red-800/50' : ''
            }`}
          />
          {errors.baseUrl && (
            <p className="mt-1 text-sm text-red-600 dark:text-red-300">{errors.baseUrl}</p>
          )}
        </div>

        <div>
          <label htmlFor="adminToken" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Admin Token *
          </label>
          <input
            type="password"
            id="adminToken"
            value={formData.adminToken}
            onChange={(e) => handleInputChange('adminToken', e.target.value)}
            placeholder="Enter your admin access token"
            className={`mt-1 block w-full rounded-md border-line-strong shadow-sm focus:border-blue-500 focus:ring-blue-500 ${
              errors.adminToken ? 'border-red-300 dark:border-red-800/50' : ''
            }`}
          />
          {errors.adminToken && (
            <p className="mt-1 text-sm text-red-600 dark:text-red-300">{errors.adminToken}</p>
          )}
          <p className="mt-1 text-xs text-content-muted">
            Generate this in Magento Admin → System → Extensions → Integrations
          </p>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label htmlFor="storeCode" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Store Code
            </label>
            <input
              type="text"
              id="storeCode"
              value={formData.storeCode}
              onChange={(e) => handleInputChange('storeCode', e.target.value)}
              placeholder="default"
              className={`mt-1 block w-full rounded-md border-line-strong shadow-sm focus:border-blue-500 focus:ring-blue-500 ${
                errors.storeCode ? 'border-red-300 dark:border-red-800/50' : ''
              }`}
            />
            {errors.storeCode && (
              <p className="mt-1 text-sm text-red-600 dark:text-red-300">{errors.storeCode}</p>
            )}
          </div>

          <div>
            <label htmlFor="version" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Magento Version
            </label>
            <SelectField
              id="version"
              value={formData.version}
              onChange={(e) => handleInputChange('version', e.target.value as '2.3' | '2.4')}
              className="mt-1 block w-full rounded-md border-line-strong shadow-sm focus:border-blue-500 focus:ring-blue-500"
            >
              <option value="2.3">Magento 2.3</option>
              <option value="2.4">Magento 2.4</option>
            </SelectField>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label htmlFor="websiteId" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Website ID
            </label>
            <input
              type="number"
              id="websiteId"
              value={formData.websiteId}
              onChange={(e) => handleInputChange('websiteId', parseInt(e.target.value) || 1)}
              min="1"
              className={`mt-1 block w-full rounded-md border-line-strong shadow-sm focus:border-blue-500 focus:ring-blue-500 ${
                errors.websiteId ? 'border-red-300 dark:border-red-800/50' : ''
              }`}
            />
            {errors.websiteId && (
              <p className="mt-1 text-sm text-red-600 dark:text-red-300">{errors.websiteId}</p>
            )}
          </div>

          <div>
            <label htmlFor="storeId" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Store ID
            </label>
            <input
              type="number"
              id="storeId"
              value={formData.storeId}
              onChange={(e) => handleInputChange('storeId', parseInt(e.target.value) || 1)}
              min="1"
              className={`mt-1 block w-full rounded-md border-line-strong shadow-sm focus:border-blue-500 focus:ring-blue-500 ${
                errors.storeId ? 'border-red-300 dark:border-red-800/50' : ''
              }`}
            />
            {errors.storeId && (
              <p className="mt-1 text-sm text-red-600 dark:text-red-300">{errors.storeId}</p>
            )}
          </div>
        </div>

        <div className="flex items-center">
          <input
            id="enableGraphQL"
            type="checkbox"
            checked={formData.enableGraphQL}
            onChange={(e) => handleInputChange('enableGraphQL', e.target.checked)}
            className="h-4 w-4 text-blue-600 dark:text-blue-300 focus:ring-blue-500 border-line-strong rounded"
          />
          <label htmlFor="enableGraphQL" className="ml-2 block text-sm text-content">
            Enable GraphQL API (Magento 2.3+ only)
          </label>
        </div>

        <div>
          <label htmlFor="webhookSecret" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Webhook Secret (Optional)
          </label>
          <input
            type="password"
            id="webhookSecret"
            value={formData.webhookSecret}
            onChange={(e) => handleInputChange('webhookSecret', e.target.value)}
            placeholder="Enter webhook secret (min 16 characters)"
            className={`mt-1 block w-full rounded-md border-line-strong shadow-sm focus:border-blue-500 focus:ring-blue-500 ${
              errors.webhookSecret ? 'border-red-300 dark:border-red-800/50' : ''
            }`}
          />
          {errors.webhookSecret && (
            <p className="mt-1 text-sm text-red-600 dark:text-red-300">{errors.webhookSecret}</p>
          )}
          <p className="mt-1 text-xs text-content-muted">
            Required for real-time webhook notifications
          </p>
        </div>
      </div>

      {testResult && (
        <div className={`p-4 rounded-md ${
          testResult.success ? 'bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800/50' : 'bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800/50'
        }`}>
          <div className="flex">
            <div className="flex-shrink-0">
              {testResult.success ? (
                <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              ) : (
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              )}
            </div>
            <div className="ml-3">
              <p className={`text-sm font-medium ${
                testResult.success ? 'text-green-800 dark:text-green-300' : 'text-red-800 dark:text-red-300'
              }`}>
                {testResult.success ? 'Connection Successful' : 'Connection Failed'}
              </p>
              <p className={`text-sm ${
                testResult.success ? 'text-green-700 dark:text-green-300' : 'text-red-700 dark:text-red-300'
              }`}>
                {testResult.message}
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="flex justify-between">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-surface border border-line-strong rounded-md hover:bg-gray-50 dark:hover:bg-dark-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleTestConnection}
          disabled={isLoading}
          className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isLoading ? 'Testing...' : 'Test Connection'}
        </button>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-content mb-4">
          Integration Configuration
        </h3>
        <p className="text-sm text-content-secondary mb-6">
          Configure how data should be synchronized between Magento and your email platform.
        </p>
      </div>

      <div className="bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800/50 rounded-md p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-green-800 dark:text-green-300">
              Connection Verified
            </h3>
            <div className="mt-2 text-sm text-green-700 dark:text-green-300">
              <p>Successfully connected to your Magento store. The integration is ready to be configured.</p>
            </div>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        <h4 className="text-md font-medium text-content">Sync Configuration</h4>
        
        <div className="grid grid-cols-1 gap-4">
          <div className="flex items-center justify-between p-4 border border-line rounded-lg">
            <div>
              <h5 className="text-sm font-medium text-content">Product Synchronization</h5>
              <p className="text-sm text-content-muted">Sync product catalog, inventory, and pricing</p>
            </div>
            <input
              type="checkbox"
              defaultChecked
              className="h-4 w-4 text-blue-600 dark:text-blue-300 focus:ring-blue-500 border-line-strong rounded"
            />
          </div>

          <div className="flex items-center justify-between p-4 border border-line rounded-lg">
            <div>
              <h5 className="text-sm font-medium text-content">Customer Synchronization</h5>
              <p className="text-sm text-content-muted">Sync customer profiles and contact information</p>
            </div>
            <input
              type="checkbox"
              defaultChecked
              className="h-4 w-4 text-blue-600 dark:text-blue-300 focus:ring-blue-500 border-line-strong rounded"
            />
          </div>

          <div className="flex items-center justify-between p-4 border border-line rounded-lg">
            <div>
              <h5 className="text-sm font-medium text-content">Order Synchronization</h5>
              <p className="text-sm text-content-muted">Sync order history and purchase data</p>
            </div>
            <input
              type="checkbox"
              defaultChecked
              className="h-4 w-4 text-blue-600 dark:text-blue-300 focus:ring-blue-500 border-line-strong rounded"
            />
          </div>

          {formData.webhookSecret && (
            <div className="flex items-center justify-between p-4 border border-line rounded-lg">
              <div>
                <h5 className="text-sm font-medium text-content">Real-time Webhooks</h5>
                <p className="text-sm text-content-muted">Enable real-time data synchronization</p>
              </div>
              <input
                type="checkbox"
                defaultChecked
                className="h-4 w-4 text-blue-600 dark:text-blue-300 focus:ring-blue-500 border-line-strong rounded"
              />
            </div>
          )}
        </div>
      </div>

      <div className="flex justify-between">
        <button
          type="button"
          onClick={() => setCurrentStep(1)}
          className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-surface border border-line-strong rounded-md hover:bg-gray-50 dark:hover:bg-dark-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          Back
        </button>
        <button
          type="button"
          onClick={handleComplete}
          className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          Complete Setup
        </button>
      </div>
    </div>
  );

  return (
    <div className="max-w-2xl mx-auto">
      <div className="mb-8">
        <div className="flex items-center">
          <div className="flex items-center text-sm text-content-muted">
            <span className={`flex items-center justify-center w-8 h-8 rounded-full border-2 ${
              currentStep >= 1 ? 'border-blue-600 bg-blue-600 text-white' : 'border-line-strong'
            }`}>
              1
            </span>
            <span className="ml-2">Connection</span>
          </div>
          <div className={`flex-1 h-0.5 mx-4 ${currentStep >= 2 ? 'bg-blue-600' : 'bg-gray-300'}`} />
          <div className="flex items-center text-sm text-content-muted">
            <span className={`flex items-center justify-center w-8 h-8 rounded-full border-2 ${
              currentStep >= 2 ? 'border-blue-600 bg-blue-600 text-white' : 'border-line-strong'
            }`}>
              2
            </span>
            <span className="ml-2">Configuration</span>
          </div>
        </div>
      </div>

      {currentStep === 1 && renderStep1()}
      {currentStep === 2 && renderStep2()}
    </div>
  );
};